import { useIsFocused } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';

import { CarouselNav } from '../common/CarouselNav';
import { Pressable } from '../common/Touchable';

type Props<T> = {
	items: T[];
	keyOf: (item: T, index: number) => string;
	/** Every card is laid out at the centre card's size and scaled down as it moves out. */
	renderItem: (item: T, index: number, isActive: boolean) => React.ReactNode;
	/** Pixel width of the carousel's viewport. */
	width: number;
	/** The centre card's pixel size. */
	cardWidth: number;
	cardHeight: number;
	/** Design-to-pixel factor, for the arrows, the gaps and the dots. */
	scale: number;
	initialIndex?: number;
	hintLabel?: string;
	onActiveIndexChange?: (index: number) => void;
};

// The mobile screens browse their cards on a swipe wheel: one card in front, its neighbours
// shrunk and turned away at the sides. The desktop layer keeps that idea with room to show it -
// the front card at full size and up to three a side, each step smaller and dimmer - and gives
// the swipe a mouse drag (the cards follow the pointer and settle on the nearest card) beside the
// desktop's own inputs: the arrows, the dots, the keyboard's arrow keys, or a click on any card at
// the side, which brings it to the front. A click on the front card is the card's own action
// (open the room, register, ...) - unless it ends a drag, which swallows it.
//
// Cards keep one layout size and are moved and scaled with CSS transitions, so the text in them
// scales smoothly rather than re-laying out on every step.
const SIDE_SCALES = [1, 0.84, 0.7, 0.58];
const SIDE_DIM = [0, 0.28, 0.46, 0.6];
const GAP = 16;
const ARROW = 56;
const NAV_GAP = 18;
const NAV_SCALE = 1.62;
const TRANSITION = 'transform 340ms cubic-bezier(0.22, 1, 0.36, 1), opacity 340ms ease-out';
/** How far the pointer moves before a press becomes a drag. */
const DRAG_SLOP = 6;
/** A release past this share of a step, or faster than FLICK_SPEED px/ms, moves at least one card. */
const DRAG_COMMIT = 0.18;
const FLICK_SPEED = 0.45;
/** Dragging past either end gives only this share of the pointer's travel. */
const EDGE_RESISTANCE = 0.3;
/** Steps past the last shown one that stay mounted, so a card slides in rather than pops in. */
const MOUNT_MARGIN = 2;

/** Reads `table` at a fractional index, linearly between its entries and held at its last. */
const lerpAt = (table: number[], at: number) => {
	const last = table.length - 1;
	if (at >= last) return table[last];
	const lo = Math.floor(at);
	return table[lo] + (table[lo + 1] - table[lo]) * (at - lo);
};

type CardBodyProps<T> = {
	item: T;
	index: number;
	isActive: boolean;
	render: Props<T>['renderItem'];
};

// A drag re-renders the strip on every pointer move to place the cards; the card's own content
// doesn't change with it, so it is held back unless its item or its place at the front does.
const CardBody = React.memo(function CardBody<T>({
	item,
	index,
	isActive,
	render,
}: CardBodyProps<T>) {
	return <>{render(item, index, isActive)}</>;
}) as <T>(props: CardBodyProps<T>) => React.ReactElement;

/** Pixel height the dots and hint take under the cards. */
export const getDesktopCoverflowNavHeight = (scale: number, withHint: boolean) =>
	scale * (NAV_GAP + NAV_SCALE * (9 + (withHint ? 6 + 16 : 0)));

export function DesktopCoverflow<T>({
	items,
	keyOf,
	renderItem,
	width,
	cardWidth,
	cardHeight,
	scale,
	initialIndex = 0,
	hintLabel,
	onActiveIndexChange,
}: Props<T>) {
	const { t } = useTranslation();
	const isFocused = useIsFocused();
	const count = items.length;
	const clampIndex = useCallback(
		(index: number) => Math.max(0, Math.min(count - 1, index)),
		[count],
	);
	const [active, setActive] = useState(() => clampIndex(initialIndex));
	// Until the player moves it, the carousel keeps to the card it is meant to open on - lists
	// that arrive in parts (the socket-fed ones) would otherwise leave it wherever the first part
	// put it.
	const touched = useRef(false);
	// Pixels the pointer has dragged the cards, or null while no drag is on.
	const [drag, setDrag] = useState<number | null>(null);
	const stripRef = useRef<View>(null);
	const s = (v: number) => v * scale;

	useEffect(() => {
		setActive(current => clampIndex(touched.current ? current : initialIndex));
	}, [clampIndex, initialIndex]);

	const go = useCallback(
		(index: number) => {
			touched.current = true;
			const next = clampIndex(index);
			setActive(next);
			onActiveIndexChange?.(next);
		},
		[clampIndex, onActiveIndexChange],
	);

	// Left / right arrow keys step the carousel - only while its screen is the one on show, since
	// the navigator keeps blurred screens mounted.
	useEffect(() => {
		if (Platform.OS !== 'web' || !isFocused) return undefined;
		const onKey = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
			if (event.key === 'ArrowLeft') go(active - 1);
			if (event.key === 'ArrowRight') go(active + 1);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [active, go, isFocused]);

	// Where each distance from the front lands: the centre of every card sits the half widths
	// of it and its neighbour plus one gap away from the next one in.
	const offsets = useMemo(() => {
		const out = [0];
		for (let step = 1; step < SIDE_SCALES.length; step++) {
			out.push(
				out[step - 1] +
					(cardWidth * SIDE_SCALES[step - 1]) / 2 +
					s(GAP) +
					(cardWidth * SIDE_SCALES[step]) / 2,
			);
		}
		return out;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cardWidth, scale]);
	const stepPx = offsets[1] ?? cardWidth;

	// Where the front sits as a fractional index: the active card, less however many steps the
	// drag has pulled, with some give past either end.
	let position = active;
	if (drag !== null) {
		position = active - drag / stepPx;
		if (position < 0) position *= EDGE_RESISTANCE;
		else if (position > count - 1)
			position = count - 1 + (position - (count - 1)) * EDGE_RESISTANCE;
	}

	// The pointer listeners are bound once per node; they read the current values from here.
	const latest = useRef({ active, stepPx, go });
	latest.current = { active, stepPx, go };

	// Drag to browse, on web. The listeners go straight on the strip's DOM node so the drag can
	// capture the pointer and swallow the click that ends it before a card's onPress sees it -
	// react-native-web fires onPress from the native click.
	useEffect(() => {
		const node = stripRef.current as unknown as HTMLElement | null;
		if (Platform.OS !== 'web' || !node || count < 2) return undefined;

		let pointerId: number | null = null;
		let startX = 0;
		let dragging = false;
		let swallowClick = false;
		let lastX = 0;
		let lastT = 0;
		let velocity = 0;

		const onDown = (event: PointerEvent) => {
			if (event.button !== 0 || pointerId !== null) return;
			pointerId = event.pointerId;
			startX = lastX = event.clientX;
			lastT = event.timeStamp;
			velocity = 0;
			dragging = false;
		};
		const onMove = (event: PointerEvent) => {
			if (event.pointerId !== pointerId) return;
			const dx = event.clientX - startX;
			if (!dragging) {
				if (Math.abs(dx) < DRAG_SLOP) return;
				dragging = true;
				try {
					// Keeps the drag going when the pointer leaves the strip.
					node.setPointerCapture(event.pointerId);
				} catch {
					// The pointer is already gone - the drag still works while it stays over the strip.
				}
			}
			const dt = event.timeStamp - lastT;
			if (dt > 0) velocity = 0.6 * ((event.clientX - lastX) / dt) + 0.4 * velocity;
			lastX = event.clientX;
			lastT = event.timeStamp;
			setDrag(dx);
		};
		const onEnd = (event: PointerEvent) => {
			if (event.pointerId !== pointerId) return;
			pointerId = null;
			if (!dragging) return;
			dragging = false;
			swallowClick = true;
			// A click that never comes (a cancel, a release off the window) must not eat the next one.
			setTimeout(() => {
				swallowClick = false;
			}, 0);
			setDrag(null);
			if (event.type === 'pointercancel') return;

			const { active: from, stepPx: step, go: goTo } = latest.current;
			const dx = event.clientX - startX;
			// A pointer that stopped before letting go is not a flick.
			const speed = event.timeStamp - lastT > 80 ? 0 : velocity;
			let target = Math.round(from - dx / step);
			if (target === from && (Math.abs(dx) > step * DRAG_COMMIT || Math.abs(speed) > FLICK_SPEED)) {
				target = from - Math.sign(dx || speed);
			}
			goTo(target);
		};
		const onClick = (event: MouseEvent) => {
			if (!swallowClick) return;
			swallowClick = false;
			event.stopPropagation();
			event.preventDefault();
		};
		// Images would start the browser's own drag-and-drop instead.
		const onDragStart = (event: DragEvent) => event.preventDefault();

		node.addEventListener('pointerdown', onDown);
		node.addEventListener('pointermove', onMove);
		node.addEventListener('pointerup', onEnd);
		node.addEventListener('pointercancel', onEnd);
		node.addEventListener('click', onClick, true);
		node.addEventListener('dragstart', onDragStart);
		return () => {
			node.removeEventListener('pointerdown', onDown);
			node.removeEventListener('pointermove', onMove);
			node.removeEventListener('pointerup', onEnd);
			node.removeEventListener('pointercancel', onEnd);
			node.removeEventListener('click', onClick, true);
			node.removeEventListener('dragstart', onDragStart);
		};
	}, [count]);

	// A step is shown only if its card fits whole between the two arrows.
	const halfRoom = width / 2 - s(ARROW) - s(GAP);
	const visibleSteps = Math.max(
		1,
		offsets.filter((offset, step) => offset + (cardWidth * SIDE_SCALES[step]) / 2 <= halfRoom + 0.5)
			.length,
	);

	const arrow = (direction: -1 | 1) => {
		const disabled = direction === -1 ? active <= 0 : active >= count - 1;

		return (
			<Pressable
				accessibilityRole='button'
				accessibilityLabel={direction === -1 ? 'Previous' : 'Next'}
				disabled={disabled}
				onPress={() => go(active + direction)}
				style={[
					styles.arrow,
					{
						width: s(ARROW),
						height: s(ARROW),
						borderRadius: s(ARROW / 2),
						top: cardHeight / 2 - s(ARROW / 2),
						[direction === -1 ? 'left' : 'right']: 0,
					},
					disabled && styles.arrowDisabled,
				]}
			>
				<Text style={[styles.arrowGlyph, { fontSize: s(34), lineHeight: s(38) }]}>
					{direction === -1 ? '‹' : '›'}
				</Text>
			</Pressable>
		);
	};

	return (
		<View style={{ width }}>
			<View
				ref={stripRef}
				style={[{ width, height: cardHeight }, styles.strip, drag !== null && styles.stripDragging]}
			>
				{items.map((item, index) => {
					// Fractional mid-drag, so every card is placed between its two resting steps.
					const distance = index - position;
					const step = Math.abs(distance);
					// Fully on show inside the shown steps, fading out over the one past them.
					const visibility = Math.max(0, Math.min(1, visibleSteps - step));
					const shown = visibility > 0;
					const cardScale = lerpAt(SIDE_SCALES, step);
					const dim = lerpAt(SIDE_DIM, step);
					// Cards past the last shown step wait just beyond it, so they slide in from the
					// edge rather than appearing out of nowhere.
					const offset =
						Math.sign(distance) * lerpAt(offsets.slice(0, visibleSteps + 1), step) || 0;
					const isActive = index === active;

					// Only the shown cards and the next one waiting past each edge are mounted: a room
					// can hold dozens of tables, and keeping them all laid out made every drag step
					// re-render the lot.
					if (step >= visibleSteps + MOUNT_MARGIN) return null;

					return (
						<View
							key={keyOf(item, index)}
							pointerEvents={shown ? 'box-none' : 'none'}
							style={[
								styles.card,
								{
									width: cardWidth,
									height: cardHeight,
									left: width / 2 - cardWidth / 2,
									zIndex: 100 - Math.round(step),
									opacity: visibility,
									transform: [{ translateX: offset }, { scale: cardScale }],
									// The cards track the pointer as it drags and ease again once it lets go.
									transition: drag === null ? TRANSITION : 'none',
								} as any,
							]}
						>
							<CardBody item={item} index={index} isActive={isActive} render={renderItem} />
							{isActive ? (
								dim > 0 ? (
									<View
										pointerEvents='none'
										style={[
											StyleSheet.absoluteFill,
											styles.sideCover,
											{ backgroundColor: `rgba(20,2,0,${dim})` },
										]}
									/>
								) : null
							) : (
								// A side card is a way to get to it, not a button of its own: the
								// press lands here and brings it to the front.
								<Pressable
									accessibilityRole='button'
									accessibilityLabel={t('T_SITE_A11Y_SHOW_CARD', {
										defaultValue: 'Show this card',
									})}
									onPress={() => go(index)}
									style={[
										StyleSheet.absoluteFill,
										styles.sideCover,
										{ backgroundColor: `rgba(20,2,0,${dim})` },
									]}
								/>
							)}
						</View>
					);
				})}
				{count > 1 ? arrow(-1) : null}
				{count > 1 ? arrow(1) : null}
			</View>

			{count > 1 ? (
				<View style={{ marginTop: s(NAV_GAP) }}>
					<CarouselNav
						count={count}
						activeIndex={active}
						onDotPress={go}
						onStep={direction => go(active + direction)}
						hintLabel={hintLabel}
						scale={scale * NAV_SCALE}
					/>
				</View>
			) : null}
		</View>
	);
}

const styles = StyleSheet.create({
	// pan-y keeps vertical page scrolling on touch screens and hands the horizontal drag to us.
	strip: {
		userSelect: 'none',
		touchAction: 'pan-y',
	} as any,
	stripDragging: {
		cursor: 'grabbing',
	} as any,
	card: {
		position: 'absolute',
		top: 0,
	},
	sideCover: {
		borderRadius: 16,
		cursor: 'pointer',
	} as any,
	// The mobile back button's disc: #4A1004 on a #FFF908 hairline with the section bloom.
	arrow: {
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		borderWidth: 1,
		borderColor: '#FFF908',
		backgroundColor: '#4A1004',
		boxShadow: '0 0 12px rgba(255,178,26,0.6)',
		cursor: 'pointer',
		zIndex: 200,
	} as any,
	arrowDisabled: {
		opacity: 0.35,
		cursor: 'default',
	} as any,
	arrowGlyph: {
		color: '#FFCC59',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		textAlign: 'center',
		marginTop: -3,
	},
});

export default DesktopCoverflow;
